import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHasPermission } from './usePermissions';
import { PERMISSIONS } from '../constants/permissions';

const PERMISSIONS_CACHE_KEY = 'cached_permissions';
const PERMISSIONS_TIMESTAMP_KEY = 'cached_permissions_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Custom hook that caches permissions in localStorage to prevent repeated checks
 * This significantly improves performance by avoiding permission API calls on every render
 */
export function useCachedPermissions() {
    const [cachedPermissions, setCachedPermissions] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Get current permission state from Redux
    const permissionsLoading = useSelector(state => state.permissions.loading);
    const permissionsError = useSelector(state => state.permissions.error);
    const hasShowInsight = useHasPermission(PERMISSIONS.SHOW_INSIGHT);
    const hasShowProject = useHasPermission(PERMISSIONS.SHOW_PROJECT);
    const hasAddProject = useHasPermission(PERMISSIONS.ADD_PROJECT);
    const hasUpdateProject = useHasPermission(PERMISSIONS.UPDATE_PROJECT);
    const hasDeleteProject = useHasPermission(PERMISSIONS.DELETE_PROJECT);

    // Unit permissions
    const hasShowUnit = useHasPermission(PERMISSIONS.SHOW_UNIT);
    const hasAddUnit = useHasPermission(PERMISSIONS.ADD_UNIT);
    const hasDeleteUnit = useHasPermission(PERMISSIONS.DELETE_UNIT);
    const hasUpdateUnit = useHasPermission(PERMISSIONS.UPDATE_UNIT);
    const hasAddCustomerUnit = useHasPermission(PERMISSIONS.ADD_CUSTOMER_UNIT);
    const hasRemoveCustomerUnit = useHasPermission(PERMISSIONS.REMOVE_CUSTOMER_UNIT);

    // Unit Updates permissions
    const hasShowUnitUpdates = useHasPermission(PERMISSIONS.SHOW_UNIT_UPDATES);
    const hasAddUnitUpdates = useHasPermission(PERMISSIONS.ADD_UNIT_UPDATES);
    const hasDeleteUnitUpdates = useHasPermission(PERMISSIONS.DELETE_UNIT_UPDATES);
    const hasUpdateUnitUpdates = useHasPermission(PERMISSIONS.UPDATE_UNIT_UPDATES);

    // Offers permissions
    const hasShowOffers = useHasPermission(PERMISSIONS.SHOW_OFFERS);
    const hasAddOffers = useHasPermission(PERMISSIONS.ADD_OFFERS);
    const hasDeleteOffers = useHasPermission(PERMISSIONS.DELETE_OFFERS);
    const hasUpdateOffers = useHasPermission(PERMISSIONS.UPDATE_OFFERS);
    const hasShowCustomerOffer = useHasPermission(PERMISSIONS.SHOW_CUSTOMER_OFFER);

    // Tickets permissions
    const hasShowTicket = useHasPermission(PERMISSIONS.SHOW_TICKET);
    const hasDeleteTicket = useHasPermission(PERMISSIONS.DELETE_TICKET);
    const hasCloseTicket = useHasPermission(PERMISSIONS.CLOSE_TICKET);
    const hasExportTicket = useHasPermission(PERMISSIONS.EXPORT_TICKET);

    // Categories permissions
    const hasShowCategory = useHasPermission(PERMISSIONS.SHOW_CATEGORY);
    const hasAddCategory = useHasPermission(PERMISSIONS.ADD_CATEGORY);
    const hasDeleteCategory = useHasPermission(PERMISSIONS.DELETE_CATEGORY);
    const hasUpdateCategory = useHasPermission(PERMISSIONS.UPDATE_CATEGORY);

    // Finance permissions
    const hasShowFinance = useHasPermission(PERMISSIONS.SHOW_FINANCE);
    const hasDeleteFinance = useHasPermission(PERMISSIONS.DELETE_FINANCE);
    const hasCloseFinance = useHasPermission(PERMISSIONS.CLOSE_FINANCE);
    const hasExportFinance = useHasPermission(PERMISSIONS.EXPORT_FINANCE);
    const hasUpdateInstallment = useHasPermission(PERMISSIONS.UPDATE_INSTALLMENT);

    // Customer permissions
    const hasShowCustomer = useHasPermission(PERMISSIONS.SHOW_CUSTOMER);
    const hasAddCustomer = useHasPermission(PERMISSIONS.ADD_CUSTOMER);
    const hasDeleteCustomer = useHasPermission(PERMISSIONS.DELETE_CUSTOMER);
    const hasUpdateCustomer = useHasPermission(PERMISSIONS.UPDATE_CUSTOMER);
    const hasExportCustomer = useHasPermission(PERMISSIONS.EXPORT_CUSTOMER);

    // Group permissions
    const hasShowGroup = useHasPermission(PERMISSIONS.SHOW_GROUP);
    const hasAddGroup = useHasPermission(PERMISSIONS.ADD_GROUP);
    const hasDeleteGroup = useHasPermission(PERMISSIONS.DELETE_GROUP);
    const hasUpdateGroup = useHasPermission(PERMISSIONS.UPDATE_GROUP);

    // Club permissions
    const hasShowClub = useHasPermission(PERMISSIONS.SHOW_CLUB);
    const hasAddClub = useHasPermission(PERMISSIONS.ADD_CLUB);
    const hasDeleteClub = useHasPermission(PERMISSIONS.DELETE_CLUB);
    const hasUpdateClub = useHasPermission(PERMISSIONS.UPDATE_CLUB);
    const hasShowCustomerClub = useHasPermission(PERMISSIONS.SHOW_CUSTOMER_CLUB);

    // Swag Updates permissions
    const hasShowSwagUpdates = useHasPermission(PERMISSIONS.SHOW_SWAG_UPDATES);
    const hasAddSwagUpdates = useHasPermission(PERMISSIONS.ADD_SWAG_UPDATES);
    const hasDeleteSwagUpdates = useHasPermission(PERMISSIONS.DELETE_SWAG_UPDATES);
    const hasUpdateSwagUpdates = useHasPermission(PERMISSIONS.UPDATE_SWAG_UPDATES);

    // Logs permissions
    const hasShowAdminLogs = useHasPermission(PERMISSIONS.SHOW_ADMIN_LOGS);
    const hasShowUserLogs = useHasPermission(PERMISSIONS.SHOW_USER_LOGS);

    // Notifications permissions
    const hasShowNotification = useHasPermission(PERMISSIONS.SHOW_NOTIFICATION);

    // Admin permissions
    const hasShowAdmins = useHasPermission(PERMISSIONS.SHOW_ADMINS);
    const hasCreateAdmins = useHasPermission(PERMISSIONS.CREATE_ADMINS);
    const hasUpdatePermission = useHasPermission(PERMISSIONS.UPDATE_PERMISSION);

    // Check if cached permissions are still valid
    const isCacheValid = useMemo(() => {
        const timestamp = localStorage.getItem(PERMISSIONS_TIMESTAMP_KEY);
        if (!timestamp) return false;

        const now = Date.now();

        const cacheTime = parseInt(timestamp, 10);
        return (now - cacheTime) < CACHE_DURATION;
    }, []);

    // Load cached permissions on mount
    useEffect(() => {
        try {
            const cached = localStorage.getItem(PERMISSIONS_CACHE_KEY);
            const timestamp = localStorage.getItem(PERMISSIONS_TIMESTAMP_KEY);

            if (cached && timestamp && isCacheValid) {
                const parsedPermissions = JSON.parse(cached);
                setCachedPermissions(parsedPermissions);
                setIsInitialized(true);
                return;
            }
        } catch (error) {
            console.warn('Failed to load cached permissions:', error);
        }

        // If no valid cache, we'll wait for fresh permissions
        setIsInitialized(false);
    }, [isCacheValid]);

    // Update cache when permissions are loaded fresh
    useEffect(() => {
        // Only update cache when permissions are successfully loaded and not loading
        if (!permissionsLoading && !permissionsError &&
            hasShowInsight !== undefined && hasShowProject !== undefined &&
            hasAddProject !== undefined && hasUpdateProject !== undefined && hasDeleteProject !== undefined &&
            hasShowUnit !== undefined && hasAddUnit !== undefined && hasDeleteUnit !== undefined &&
            hasUpdateUnit !== undefined && hasAddCustomerUnit !== undefined &&
            hasRemoveCustomerUnit !== undefined &&
            hasShowUnitUpdates !== undefined && hasAddUnitUpdates !== undefined &&
            hasDeleteUnitUpdates !== undefined && hasUpdateUnitUpdates !== undefined &&
            hasShowOffers !== undefined && hasAddOffers !== undefined && hasDeleteOffers !== undefined &&
            hasUpdateOffers !== undefined && hasShowCustomerOffer !== undefined &&
            hasShowTicket !== undefined && hasDeleteTicket !== undefined && hasCloseTicket !== undefined &&
            hasExportTicket !== undefined &&
            hasShowCategory !== undefined && hasAddCategory !== undefined && hasDeleteCategory !== undefined &&
            hasUpdateCategory !== undefined &&
            hasShowFinance !== undefined && hasDeleteFinance !== undefined && hasCloseFinance !== undefined &&
            hasExportFinance !== undefined &&
            hasShowCustomer !== undefined && hasAddCustomer !== undefined && hasDeleteCustomer !== undefined &&
            hasUpdateCustomer !== undefined && hasExportCustomer !== undefined &&
            hasShowGroup !== undefined && hasAddGroup !== undefined && hasDeleteGroup !== undefined &&
            hasUpdateGroup !== undefined &&
            hasShowClub !== undefined && hasAddClub !== undefined && hasDeleteClub !== undefined &&
            hasUpdateClub !== undefined && hasShowCustomerClub !== undefined &&
            hasShowSwagUpdates !== undefined && hasAddSwagUpdates !== undefined && hasDeleteSwagUpdates !== undefined &&
            hasUpdateSwagUpdates !== undefined && hasShowAdminLogs !== undefined && hasShowUserLogs !== undefined &&
            hasShowNotification !== undefined && hasShowAdmins !== undefined && hasCreateAdmins !== undefined &&
            hasUpdatePermission !== undefined) {
            const permissions = {
                hasShowInsight,
                hasShowProject,
                hasAddProject,
                hasUpdateProject,
                hasDeleteProject,
                hasShowUnit,
                hasAddUnit,
                hasDeleteUnit,
                hasUpdateUnit,
                hasAddCustomerUnit,
                hasRemoveCustomerUnit,
                hasShowUnitUpdates,
                hasAddUnitUpdates,
                hasDeleteUnitUpdates,
                hasUpdateUnitUpdates,
                hasShowOffers,
                hasAddOffers,
                hasDeleteOffers,
                hasUpdateOffers,
                hasShowCustomerOffer,
                hasShowTicket,
                hasDeleteTicket,
                hasCloseTicket,
                hasExportTicket,
                hasShowCategory,
                hasAddCategory,
                hasDeleteCategory,
                hasUpdateCategory,
                hasShowFinance,
                hasDeleteFinance,
                hasCloseFinance,
                hasExportFinance,
                hasUpdateInstallment,
                hasShowCustomer,
                hasAddCustomer,
                hasDeleteCustomer,
                hasUpdateCustomer,
                hasExportCustomer,
                hasShowGroup,
                hasAddGroup,
                hasDeleteGroup,
                hasUpdateGroup,
                hasShowClub,
                hasAddClub,
                hasDeleteClub,
                hasUpdateClub,
                hasShowCustomerClub,
                hasShowSwagUpdates,
                hasAddSwagUpdates,
                hasDeleteSwagUpdates,
                hasUpdateSwagUpdates,
                hasShowAdminLogs,
                hasShowUserLogs,
                hasShowNotification,
                hasShowAdmins,
                hasCreateAdmins,
                hasUpdatePermission,
                timestamp: Date.now()
            };

            try {
                localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(permissions));
                localStorage.setItem(PERMISSIONS_TIMESTAMP_KEY, permissions.timestamp.toString());
                setCachedPermissions(permissions);
                setIsInitialized(true);
            } catch (error) {
                console.warn('Failed to cache permissions:', error);
            }
        }
    }, [hasShowInsight, hasShowProject, hasAddProject, hasUpdateProject, hasDeleteProject,
        hasShowUnit, hasAddUnit, hasDeleteUnit, hasUpdateUnit, hasAddCustomerUnit,
        hasShowUnitUpdates, hasAddUnitUpdates, hasDeleteUnitUpdates, hasUpdateUnitUpdates,
        hasShowOffers, hasAddOffers, hasDeleteOffers, hasUpdateOffers, hasShowCustomerOffer,
        hasShowTicket, hasDeleteTicket, hasCloseTicket, hasExportTicket,
        hasShowCategory, hasAddCategory, hasDeleteCategory, hasUpdateCategory,
        hasShowFinance, hasDeleteFinance, hasCloseFinance, hasExportFinance, hasUpdateInstallment,
        hasShowCustomer, hasAddCustomer, hasDeleteCustomer, hasUpdateCustomer, hasExportCustomer,
        hasShowClub, hasAddClub, hasDeleteClub, hasUpdateClub, hasShowCustomerClub,
        hasShowSwagUpdates, hasAddSwagUpdates, hasDeleteSwagUpdates, hasUpdateSwagUpdates,
        hasShowAdminLogs, hasShowUserLogs, hasShowNotification, hasShowAdmins, hasCreateAdmins, hasUpdatePermission,
        permissionsLoading, permissionsError]);

    // Clear cache when there's an auth change (user logout/login)
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    useEffect(() => {
        if (!isAuthenticated) {
            localStorage.removeItem(PERMISSIONS_CACHE_KEY);
            localStorage.removeItem(PERMISSIONS_TIMESTAMP_KEY);
            setCachedPermissions(null);
            setIsInitialized(false);
            console.log('🗑️ Cleared permissions cache on logout');
        }
    }, [isAuthenticated]);

    // Return cached permissions if available, otherwise fall back to fresh data
    return useMemo(() => {
        if (cachedPermissions && isInitialized) {
            return {
                hasShowInsight: cachedPermissions.hasShowInsight,
                hasShowProject: cachedPermissions.hasShowProject,
                hasAddProject: cachedPermissions.hasAddProject,
                hasUpdateProject: cachedPermissions.hasUpdateProject,
                hasDeleteProject: cachedPermissions.hasDeleteProject,
                hasShowUnit: cachedPermissions.hasShowUnit,
                hasAddUnit: cachedPermissions.hasAddUnit,
                hasDeleteUnit: cachedPermissions.hasDeleteUnit,
                hasUpdateUnit: cachedPermissions.hasUpdateUnit,
                hasAddCustomerUnit: cachedPermissions.hasAddCustomerUnit,
                hasRemoveCustomerUnit: cachedPermissions.hasRemoveCustomerUnit,
                hasShowUnitUpdates: cachedPermissions.hasShowUnitUpdates,
                hasAddUnitUpdates: cachedPermissions.hasAddUnitUpdates,
                hasDeleteUnitUpdates: cachedPermissions.hasDeleteUnitUpdates,
                hasUpdateUnitUpdates: cachedPermissions.hasUpdateUnitUpdates,
                hasShowOffers: cachedPermissions.hasShowOffers,
                hasAddOffers: cachedPermissions.hasAddOffers,
                hasDeleteOffers: cachedPermissions.hasDeleteOffers,
                hasUpdateOffers: cachedPermissions.hasUpdateOffers,
                hasShowCustomerOffer: cachedPermissions.hasShowCustomerOffer,
                hasShowTicket: cachedPermissions.hasShowTicket,
                hasDeleteTicket: cachedPermissions.hasDeleteTicket,
                hasCloseTicket: cachedPermissions.hasCloseTicket,
                hasExportTicket: cachedPermissions.hasExportTicket,
                hasShowCategory: cachedPermissions.hasShowCategory,
                hasAddCategory: cachedPermissions.hasAddCategory,
                hasDeleteCategory: cachedPermissions.hasDeleteCategory,
                hasUpdateCategory: cachedPermissions.hasUpdateCategory,
                hasShowFinance: cachedPermissions.hasShowFinance,
                hasDeleteFinance: cachedPermissions.hasDeleteFinance,
                hasCloseFinance: cachedPermissions.hasCloseFinance,
                hasExportFinance: cachedPermissions.hasExportFinance,
                hasUpdateInstallment: cachedPermissions.hasUpdateInstallment,
                hasShowCustomer: cachedPermissions.hasShowCustomer,
                hasAddCustomer: cachedPermissions.hasAddCustomer,
                hasDeleteCustomer: cachedPermissions.hasDeleteCustomer,
                hasUpdateCustomer: cachedPermissions.hasUpdateCustomer,
                hasExportCustomer: cachedPermissions.hasExportCustomer,
                hasShowGroup: cachedPermissions.hasShowGroup,
                hasAddGroup: cachedPermissions.hasAddGroup,
                hasDeleteGroup: cachedPermissions.hasDeleteGroup,
                hasUpdateGroup: cachedPermissions.hasUpdateGroup,
                hasShowClub: cachedPermissions.hasShowClub,
                hasAddClub: cachedPermissions.hasAddClub,
                hasDeleteClub: cachedPermissions.hasDeleteClub,
                hasUpdateClub: cachedPermissions.hasUpdateClub,
                hasShowCustomerClub: cachedPermissions.hasShowCustomerClub,
                hasShowSwagUpdates: cachedPermissions.hasShowSwagUpdates,
                hasAddSwagUpdates: cachedPermissions.hasAddSwagUpdates,
                hasDeleteSwagUpdates: cachedPermissions.hasDeleteSwagUpdates,
                hasUpdateSwagUpdates: cachedPermissions.hasUpdateSwagUpdates,
                hasShowAdminLogs: cachedPermissions.hasShowAdminLogs,
                hasShowUserLogs: cachedPermissions.hasShowUserLogs,
                hasShowNotification: cachedPermissions.hasShowNotification,
                hasShowAdmins: cachedPermissions.hasShowAdmins,
                hasCreateAdmins: cachedPermissions.hasCreateAdmins,
                hasUpdatePermission: cachedPermissions.hasUpdatePermission,
                permissionsLoading: false,
                permissionsError: null,
                isReady: true,
                fromCache: true
            };
        }

        // Fall back to fresh permissions
        return {
            hasShowInsight,
            hasShowProject,
            hasAddProject,
            hasUpdateProject,
            hasDeleteProject,
            hasShowUnit,
            hasAddUnit,
            hasDeleteUnit,
            hasUpdateUnit,
            hasAddCustomerUnit,
            hasRemoveCustomerUnit,
            hasShowUnitUpdates,
            hasAddUnitUpdates,
            hasDeleteUnitUpdates,
            hasUpdateUnitUpdates,
            hasShowOffers,
            hasAddOffers,
            hasDeleteOffers,
            hasUpdateOffers,
            hasShowCustomerOffer,
            hasShowTicket,
            hasDeleteTicket,
            hasCloseTicket,
            hasExportTicket,
            hasShowCategory,
            hasAddCategory,
            hasDeleteCategory,
            hasUpdateCategory,
            hasShowFinance,
            hasDeleteFinance,
            hasCloseFinance,
            hasExportFinance,
            hasShowCustomer,
            hasAddCustomer,
            hasDeleteCustomer,
            hasUpdateCustomer,
            hasExportCustomer,
            hasShowGroup,
            hasAddGroup,
            hasDeleteGroup,
            hasUpdateGroup,
            hasShowClub,
            hasAddClub,
            hasDeleteClub,
            hasUpdateClub,
            hasShowCustomerClub,
            hasShowSwagUpdates,
            hasAddSwagUpdates,
            hasDeleteSwagUpdates,
            hasUpdateSwagUpdates,
            hasShowAdminLogs,
            hasShowUserLogs,
            hasShowNotification,
            hasShowAdmins,
            hasCreateAdmins,
            hasUpdatePermission,
            permissionsLoading,
            permissionsError,
            isReady: !permissionsLoading && !permissionsError &&
                hasShowInsight !== undefined && hasShowProject !== undefined &&
                hasAddProject !== undefined && hasUpdateProject !== undefined && hasDeleteProject !== undefined &&
                hasShowUnit !== undefined && hasAddUnit !== undefined && hasDeleteUnit !== undefined &&
                hasUpdateUnit !== undefined && hasAddCustomerUnit !== undefined &&
                hasRemoveCustomerUnit !== undefined &&
                hasShowUnitUpdates !== undefined && hasAddUnitUpdates !== undefined &&
                hasDeleteUnitUpdates !== undefined && hasUpdateUnitUpdates !== undefined &&
                hasShowOffers !== undefined && hasAddOffers !== undefined && hasDeleteOffers !== undefined &&
                hasUpdateOffers !== undefined && hasShowCustomerOffer !== undefined &&
                hasShowTicket !== undefined && hasDeleteTicket !== undefined && hasCloseTicket !== undefined &&
                hasExportTicket !== undefined &&
                hasShowCategory !== undefined && hasAddCategory !== undefined && hasDeleteCategory !== undefined &&
                hasUpdateCategory !== undefined &&
                hasShowFinance !== undefined && hasDeleteFinance !== undefined && hasCloseFinance !== undefined &&
                hasExportFinance !== undefined && hasUpdateInstallment !== undefined &&
                hasShowCustomer !== undefined && hasAddCustomer !== undefined && hasDeleteCustomer !== undefined &&
                hasUpdateCustomer !== undefined && hasExportCustomer !== undefined &&
                hasShowGroup !== undefined && hasAddGroup !== undefined && hasDeleteGroup !== undefined &&
                hasUpdateGroup !== undefined &&
                hasShowClub !== undefined && hasAddClub !== undefined && hasDeleteClub !== undefined &&
                hasUpdateClub !== undefined && hasShowCustomerClub !== undefined &&
                hasShowSwagUpdates !== undefined && hasAddSwagUpdates !== undefined &&
                hasDeleteSwagUpdates !== undefined && hasUpdateSwagUpdates !== undefined &&
                hasShowAdminLogs !== undefined && hasShowUserLogs !== undefined && hasShowNotification !== undefined &&
                hasShowAdmins !== undefined && hasCreateAdmins !== undefined && hasUpdatePermission !== undefined,
            fromCache: false
        };
    }, [cachedPermissions, isInitialized, hasShowInsight, hasShowProject, hasAddProject, hasUpdateProject, hasDeleteProject,
        hasShowUnit, hasAddUnit, hasDeleteUnit, hasUpdateUnit, hasAddCustomerUnit,
        hasShowUnitUpdates, hasAddUnitUpdates, hasDeleteUnitUpdates, hasUpdateUnitUpdates,
        hasShowOffers, hasAddOffers, hasDeleteOffers, hasUpdateOffers, hasShowCustomerOffer,
        hasShowTicket, hasDeleteTicket, hasCloseTicket, hasExportTicket,
        hasShowCategory, hasAddCategory, hasDeleteCategory, hasUpdateCategory,
        hasShowFinance, hasDeleteFinance, hasCloseFinance, hasExportFinance, hasUpdateInstallment,
        hasShowCustomer, hasAddCustomer, hasDeleteCustomer, hasUpdateCustomer, hasExportCustomer,
        hasShowClub, hasAddClub, hasDeleteClub, hasUpdateClub, hasShowCustomerClub,
        hasShowSwagUpdates, hasAddSwagUpdates, hasDeleteSwagUpdates, hasUpdateSwagUpdates,
        hasShowAdminLogs, hasShowUserLogs, hasShowNotification, hasShowAdmins, hasCreateAdmins, hasUpdatePermission,
        permissionsLoading, permissionsError]);
}

/**
 * Utility function to clear permissions cache manually
 */
export function clearPermissionsCache() {
    localStorage.removeItem(PERMISSIONS_CACHE_KEY);
    localStorage.removeItem(PERMISSIONS_TIMESTAMP_KEY);
    console.log('🗑️ Manually cleared permissions cache');
}
